import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';

import {
  attachReceiptEvidence,
  attachResourceChainEvidence,
  confirmOutboundReceipt,
  confirmOutboundResourceChain,
  confirmOutboundSalesResult,
  createOutbound,
  dispatchOutbound,
  fetchBuyerProposals,
  fetchOutbounds,
  lockOutbound,
  updateOutbound,
  voidOutbound,
  waiveOutboundReceipt,
  waiveOutboundResourceChain,
} from './api';
import type {
  BuyerProposalOption,
  ConfirmOutboundSalesResultInput,
  OutboundDraftInput,
  OutboundDto,
  OutboundListFilters,
  OutboundListMeta,
  UpdateOutboundDraftInput,
} from './types';

const KEY = 'outbounds' as const;

const useInvalidateOutbounds = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [KEY] });
};

export const useOutbounds = (batchId: string | undefined, filters: OutboundListFilters = {}) =>
  useQuery<{ data: OutboundDto[]; meta: OutboundListMeta }, ApiClientError>({
    queryKey: [KEY, 'list', batchId, filters],
    queryFn: () => fetchOutbounds(batchId!, filters),
    enabled: !!batchId,
    placeholderData: (previous) => previous,
  });

export const useBuyerProposals = (batchId: string | undefined, enabled: boolean) =>
  useQuery<BuyerProposalOption[], ApiClientError>({
    queryKey: ['buyer-proposals', batchId],
    queryFn: () => fetchBuyerProposals(batchId!),
    enabled: !!batchId && enabled,
  });

export const useCreateOutbound = (batchId: string) => {
  const invalidate = useInvalidateOutbounds();
  return useMutation<OutboundDto, ApiClientError, OutboundDraftInput>({
    mutationFn: (body) => createOutbound(batchId, body),
    onSuccess: invalidate,
  });
};

export const useUpdateOutbound = () => {
  const invalidate = useInvalidateOutbounds();
  return useMutation<OutboundDto, ApiClientError, { id: string; body: UpdateOutboundDraftInput }>({
    mutationFn: ({ id, body }) => updateOutbound(id, body),
    onSuccess: invalidate,
  });
};

export const useVoidOutbound = () => {
  const invalidate = useInvalidateOutbounds();
  return useMutation<OutboundDto, ApiClientError, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => voidOutbound(id, reason),
    onSuccess: invalidate,
  });
};

const useIdTransition = (fn: (id: string) => Promise<OutboundDto>) => {
  const invalidate = useInvalidateOutbounds();
  return useMutation<OutboundDto, ApiClientError, string>({
    mutationFn: fn,
    onSuccess: invalidate,
  });
};

const useEvidenceTransition = (
  fn: (id: string, evidenceFileId: string) => Promise<OutboundDto>,
) => {
  const invalidate = useInvalidateOutbounds();
  return useMutation<OutboundDto, ApiClientError, { id: string; evidenceFileId: string }>({
    mutationFn: ({ id, evidenceFileId }) => fn(id, evidenceFileId),
    onSuccess: invalidate,
  });
};

const useReasonTransition = (fn: (id: string, reason: string) => Promise<OutboundDto>) => {
  const invalidate = useInvalidateOutbounds();
  return useMutation<OutboundDto, ApiClientError, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => fn(id, reason),
    onSuccess: invalidate,
  });
};

export const useDispatchOutbound = () => useEvidenceTransition(dispatchOutbound);
export const useAttachReceiptEvidence = () => useEvidenceTransition(attachReceiptEvidence);
export const useAttachResourceChainEvidence = () =>
  useEvidenceTransition(attachResourceChainEvidence);
export const useConfirmOutboundReceipt = () => useIdTransition(confirmOutboundReceipt);
export const useWaiveOutboundReceipt = () => useReasonTransition(waiveOutboundReceipt);
export const useConfirmOutboundResourceChain = () => useIdTransition(confirmOutboundResourceChain);
export const useWaiveOutboundResourceChain = () => useReasonTransition(waiveOutboundResourceChain);
export const useConfirmOutboundSalesResult = () => {
  const invalidate = useInvalidateOutbounds();
  return useMutation<
    OutboundDto,
    ApiClientError,
    { id: string; body: ConfirmOutboundSalesResultInput }
  >({
    mutationFn: ({ id, body }) => confirmOutboundSalesResult(id, body),
    onSuccess: invalidate,
  });
};
export const useLockOutbound = () => useIdTransition(lockOutbound);
